import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import { CheckCircle2, Bed, Stethoscope, Video, Phone, FileText, ShieldCheck, AlertTriangle, ArrowRight, IndianRupee } from "lucide-react";

const features = [
  { icon: Bed, title: "Hospital Bed Booking", desc: "Book SOS (emergency) or scheduled hospital beds. A commission charge based on bed type is collected at booking and is refundable if you do not get admitted." },
  { icon: Stethoscope, title: "Home Healthcare", desc: "Book doctors, nurses, physiotherapists, dieticians, medical technologists or ayahs for home visits. Pay online through the app." },
  { icon: Video, title: "Video Consultations", desc: "Speak to hospital doctors, physios, dieticians and HomeCure home-care doctors / dieticians anytime. Nurses & physios are available for video after their first in-person visit. Ayahs are not available for video." },
  { icon: Phone, title: "Masked Audio Calls", desc: "Talk to healthcare personnel through a virtual proxy number — your real phone number is never shared." },
  { icon: FileText, title: "Health Records Vault", desc: "Securely store prescriptions, lab reports, scans and discharge summaries. Encrypted at rest, accessible only to you and personnel you authorise." },
  { icon: ShieldCheck, title: "Health Profile", desc: "Save comorbidities, allergies, medications and emergency contacts — shared with hospitals only with your explicit consent." },
];

const rate = [
  "Home healthcare personnel after each appointment",
  "Hospital doctors, physios & dieticians (in-person & video)",
  "Hospital departments and the treating hospital doctor",
  "The hospital overall — after your stay is complete",
];

export default function ForPatients() {
  return (
    <>
      <PageHero
        title="For Patients"
        subtitle="One app for hospital beds, home visits and instant video consultations — with all your records in a secure vault."
      />

      <Section eyebrow="What you can do" title="Everything a patient needs — in one place" center>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="hc-card p-6">
              <div className="w-11 h-11 rounded-lg bg-[#E8F0FB] flex items-center justify-center text-[#3780E0]">
                <f.icon className="w-5 h-5" />
              </div>
              <div className="mt-3 font-bold text-lg text-[#0F2A4A]">{f.title}</div>
              <div className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="light" eyebrow="How payments work" title="Transparent, commission-based billing">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="hc-card p-6 bg-white">
            <IndianRupee className="w-9 h-9 text-[#3780E0]" />
            <div className="mt-3 text-lg font-bold text-[#0F2A4A]">Consultations &amp; Home Care</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Full payment online through the app for in-person and video consultations of hospital doctors/physios/dieticians</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Full payment online through the app for home healthcare services</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> If a booking is rejected, full refund within 7 working days</li>
            </ul>
          </div>
          <div className="hc-card p-6 bg-white">
            <Bed className="w-9 h-9 text-[#3780E0]" />
            <div className="mt-3 text-lg font-bold text-[#0F2A4A]">Hospital Bed Bookings</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Only a commission charge (based on bed type) is paid through HomeCure</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Commission is refunded if you do not get admitted</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#3780E0]" /> Final hospital bill is paid directly at the hospital</li>
            </ul>
            <div className="mt-4 rounded-lg bg-[#FFF3E0] border border-[#FFE0B2] p-3 text-xs text-[#7A4F1B] flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>HomeCure never represents, estimates or guarantees the total cost of hospital treatment. Hospital billing is independent.</span>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="Reviews & ratings" title="Your voice matters" subtitle="After every completed appointment or hospital stay, you can rate and review:">
        <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
          {rate.map((r, i) => (
            <div key={i} className="flex items-start gap-3 bg-[#E8F0FB] border border-[#C3D9F6] rounded-lg p-4">
              <CheckCircle2 className="w-5 h-5 text-[#3780E0] mt-0.5" />
              <span className="text-sm text-[#0F2A4A]">{r}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="primary" center>
        <h2 className="text-white text-3xl md:text-4xl font-extrabold">Be the first to use HomeCure</h2>
        <p className="text-white/90 mt-3">We will notify you when the app is live in your city.</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="bg-white text-[#2563B3] hover:bg-slate-100 font-bold px-6 py-3 rounded-lg inline-flex items-center gap-2">Notify me <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </Section>
    </>
  );
}
